import { AsyncPipe, JsonPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, Type, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap/dropdown';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import { periodToSlice } from 'src/app/dashboards/models/period';
import { PanelService } from 'src/app/dashboards/panels/services/panel/panel.service';
import { GraphPlaceholderComponent } from 'src/app/panels/components/graph-placeholder/graph-placeholder.component';
import { GraphComponent } from 'src/app/panels/components/graph/graph.component';
import { PanelComponent } from 'src/app/panels/components/panel.component';
import { VisibleDirective, waitUntilVisible } from 'src/app/panels/directives/visible/visible.directive';
import { DisplayUnit } from 'src/app/panels/models/display-unit';
import { IGraph, IGraphConfig, IGraphInfo, IGraphMappedData, IGraphMappedDataset } from 'src/app/panels/models/graph';
import { CardComponent } from 'src/app/shared/components/card/card.component';
import { ErrorMessageComponent } from 'src/app/shared/components/error-message/error-message.component';
import { MessageComponent } from 'src/app/shared/components/message/message.component';
import { ITab, TabsComponent } from 'src/app/shared/components/tabs/tabs.component';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { UnsModalService } from 'src/app/shared/modals/service/uns-modal.service';
import { PanelStateService } from '../../services/panel-state.service';

export const GRAPH_PANEL_IMPORTS: Type<unknown>[] = [
  CardComponent,
  VisibleDirective,
  TabsComponent,
  ToggleComponent,
  NgbDropdown,
  NgbDropdownToggle,
  NgbDropdownMenu,
  FormsModule,
  GraphComponent,
  ErrorMessageComponent,
  AsyncPipe,
  JsonPipe,
  MessageComponent,
  GraphPlaceholderComponent,
];

@Component({
  selector: 'app-graph-panel',
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export abstract class GraphPanelComponent extends PanelComponent implements OnInit, OnDestroy {
  @HostBinding('class') protected class = 'col-12';
  public small = false;

  protected override data$?: Observable<IGraph | null>;

  protected graphData$!: Observable<IGraphMappedData | null>;
  protected placeholderData = [0, 0, 0, 0, 0, 0, 0, 2, 3, 5, 6, 7, 10, 8, 7, 5, 6, 3, 1, 0, 0, 0, 0, 0];

  protected zoomEnabled$ = new BehaviorSubject<boolean>(false);
  protected showAnnotations$ = new BehaviorSubject<boolean>(true);
  protected showM2$ = new BehaviorSubject<boolean>(false);
  protected tabs: ITab[] = [];

  protected selectedTab$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  protected modalService = inject(UnsModalService);

  protected readonly graphInfo?: IGraphInfo;
  protected panelService = inject(PanelService);
  protected panelStateService = inject(PanelStateService);

  private selectedTabSubscription?: Subscription;

  protected abstract readonly unit: DisplayUnit;
  protected abstract config: IGraphConfig;

  ngOnInit(): void {
    this.data$ = combineLatest([this.dashboardParams$, this.visible$]).pipe(
      waitUntilVisible(this),
      switchMap((params) => {
        const panelData = this.panelService
          .getGraph(
            this.getType(),
            this.entityIds,
            params.timeRange.from,
            params.timeRange.till,
            periodToSlice(params.period!),
            params.timeRange.compareFrom,
            params.timeRange.compareTill,
          )
          .pipe(
            catchError((err) => {
              this.error = 'Geen data';
              console.error(`ERROR MESSAGE: ${err.type} - ${err.message}`);
              return of(null);
            }),
          );
        if (params.period == 'live') {
          return this.dropFutureValues(panelData);
        }

        return panelData;
      }),
      tap((data) => {
        this.loading = false;
        if (data?.telemetryCharts.graph.graphs.length !== 0) {
          this.tabs =
            data?.telemetryCharts.graph.graphs
              .flatMap((x) => x.data)
              .map((x) => ({ name: x.name }) as ITab)
              .filter((value, index, array) => array.map((x) => x.name).indexOf(value.name) == index) ?? [];

          // Try to restore previously saved selection for this panel
          const saved = this.panelStateService.get(this.getPersistenceKey());

          if (saved && this.tabs.some((t) => t.name === saved)) {
            this.selectedTab$.next(saved);
          } else if (!this.selectedTab$.value && this.tabs.length > 0) {
            this.selectedTab$.next(this.tabs[0].name);
          } else if (this.selectedTab$.value !== this.tabs[0].name) {
            this.selectedTab$.next(this.tabs[0].name);
          }
        } else {
          this.tabs = [];
          this.selectedTab$.next(null);
        }
      }),
      shareReplay(1),
    );

    this.graphData$ = combineLatest([this.data$, this.selectedTab$]).pipe(
      map(([data, selectedTab]) => {
        if (!data) return null;

        const annotations = data?.telemetryCharts.graph.graphs
          .flatMap((value) => value.data.find((x) => x.name === selectedTab)?.annotations)
          .filter((x) => x);

        return {
          annotations: annotations.length ? annotations : undefined,
          datasets: data?.telemetryCharts.graph.graphs.flatMap((value, index) => {
            const datasets = value.data.find((x) => x.name === selectedTab)?.datasets;

            if (!datasets) return [];

            return datasets.map(
              (dataset) =>
                ({
                  entityIndex: index,
                  entityName: value.name,
                  type: dataset.type,
                  records: dataset.records,
                }) as IGraphMappedDataset,
            );
          }),
        } as IGraphMappedData;
      }),
    );

    this.selectedTabSubscription = this.selectedTab$
      .pipe(
        filter((v): v is string => v !== null),
        distinctUntilChanged(),
      )
      .subscribe((val) => {
        this.panelStateService.set(this.getPersistenceKey(), val);
      });

    if (this.small) {
      this.class = 'col-12 col-xl-6';
    }
  }

  ngOnDestroy(): void {
    this.selectedTabSubscription?.unsubscribe();
  }

  /**
   * Determines whether show annotations changed on
   * @param selected
   */
  protected toggleAnnotations(selected: boolean): void {
    this.showAnnotations$?.next(selected);
  }

  /**
   * Enable/disable zoom
   * @param selected
   */
  protected toggleZoom(selected: boolean): void {
    this.zoomEnabled$?.next(selected);
  }

  /**
   * Determines whether view usage per m2 changed on
   * @param selected
   */
  protected toggleM2(selected: boolean): void {
    this.showM2$.next(selected);
  }

  protected showGraphInfo(graphInfo: IGraphInfo): void {
    this.modalService.openInfoModal(graphInfo.title, graphInfo.description, undefined, { size: 'lg' }, false);
  }

  private dropFutureValues(data$: Observable<IGraph | null>): Observable<IGraph | null> {
    const now = new Date();
    // Data is delayed and thus we must check entries are from 15 minutes before instead of now
    const fifteenMinutesBefore = new Date(now.getTime() - 5 * 60000);

    return data$.pipe(
      map((graph) => {
        if (!graph) {
          return null;
        }
        // Deep clone to avoid mutating original data
        const clonedGraph = structuredClone(graph) as IGraph;
        clonedGraph.telemetryCharts.graph.graphs = clonedGraph.telemetryCharts.graph.graphs.map((graphValue) => {
          graphValue.data = graphValue.data.map((data) => {
            data.datasets = data.datasets.map((dataset) => {
              dataset.records = dataset.records.filter((record) => {
                return new Date(record.start) < fifteenMinutesBefore;
              });
              return dataset;
            });
            return data;
          });
          return graphValue;
        });
        return clonedGraph;
      }),
    );
  }

  /**
   * Build a stable key to persist UI state for this panel within the current session.
   * Uses path + panel type + first entity id(s) so it survives simple re-creation while
   * keeping the key scoped to the visible dashboard and panel.
   */
  private getPersistenceKey(): string {
    const type = this.getType() ? String(this.getType()) : 'panel';
    const entities = (this.entityIds || []).join(',');
    return `graph:selectedTab|${type}|${entities}`;
  }
}
